import { useLDClient } from "launchdarkly-react-client-sdk";
import { LDSingleKindContext } from "launchdarkly-js-sdk-common";
import { SessionKf } from "components/auth/lib/SessionKf";
import { useSessionKf } from "components/auth/lib/useSessionKf";
import { createContext, useContext, useEffect, useState } from "react";

const LaunchDarklyReadyContext = createContext(false);

function getLaunchDarklyContext(
  session: SessionKf,
): LDSingleKindContext | { anonymous: true } {
  const email = session?.user?.name;
  if (email)
    return {
      kind: "user",
      key: email,
      email: email,
      name: [session.user.given_name, session.user.family_name]
        .filter((x) => !!x)
        .join(" "),
    };
  else
    return {
      anonymous: true,
    };
}

function useLaunchDarklyUserContext() {
  const launchDarklyClient = useLDClient();
  const { session, status } = useSessionKf();

  const isAuthLoading = status === "loading";
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!launchDarklyClient || isAuthLoading) return;
    const current = launchDarklyClient.getContext();
    const next = getLaunchDarklyContext(session);
    const shouldIdentify =
      !current ||
      current.anonymous !== next.anonymous ||
      (!next.anonymous && current.key !== next["key"]);
    if (!shouldIdentify) {
      setIsReady(true);
      return;
    }

    setIsReady(false);
    let isCancelled = false;
    const markReady = () => {
      if (!isCancelled) setIsReady(true);
    };
    // A failed identify leaves the previous flags in place, which are still usable.
    launchDarklyClient.identify(next).then(markReady, markReady);

    return () => {
      isCancelled = true;
    };
  }, [session, isAuthLoading, launchDarklyClient]);

  return isReady;
}

export function LaunchDarklyProvider({ children }: any) {
  const isReady = useLaunchDarklyUserContext();

  return (
    <LaunchDarklyReadyContext.Provider value={isReady}>
      {children}
    </LaunchDarklyReadyContext.Provider>
  );
}

/**
 * Whether flags have loaded for the signed-in user. Until then, `useFlags()`
 * can still change as the client loads and identifies the user.
 */
export function useIsLaunchDarklyReady() {
  return useContext(LaunchDarklyReadyContext);
}
